import { useCallback } from 'react';
import type { AxiosInstance } from 'axios';
import { doGetDocumentPdfFile } from '../../../U3141Service';
import {
  u3141DecodeDocumentPdfFileAsText,
  u3141IsHtmlMime,
  u3141InferDocumentMime,
  u3141OpenDocumentPdfFileInNewTab,
  type U3141DocumentPdfFilePayload,
} from '../../../tools/u3141OpenDocumentPdfFile';

export interface U3141UseOpenDocumentPdfFileHandlersParams {
  axios: AxiosInstance;
  onError?: (errors: { error: unknown }) => void;
}

export interface U3141UseOpenDocumentPdfFileHandlersReturn {
  /** Charge pdf-file et ouvre dans un nouvel onglet (PDF ou HTML). */
  openDocumentPdfFile: (documentId: string) => Promise<boolean>;
  /** Charge pdf-file et retourne le payload (ex. historique → dialog HTML). */
  loadDocumentPdfFile: (documentId: string) => Promise<U3141DocumentPdfFilePayload | null>;
  /** Charge pdf-file HTML et retourne le texte décodé ; null si non-HTML. */
  loadDocumentPdfFileAsHtmlText: (documentId: string) => Promise<string | null>;
}

/**
 * Handler dédié : GET pdf-file (NAS/cache) sans appeler do* depuis les composants.
 */
export function useU3141OpenDocumentPdfFileHandlers({
  axios,
  onError,
}: U3141UseOpenDocumentPdfFileHandlersParams): U3141UseOpenDocumentPdfFileHandlersReturn {
  const loadDocumentPdfFile = useCallback(
    async (documentId: string): Promise<U3141DocumentPdfFilePayload | null> => {
      try {
        return await doGetDocumentPdfFile(axios, documentId);
      } catch (error) {
        onError?.({ error });
        return null;
      }
    },
    [axios, onError]
  );

  const openDocumentPdfFile = useCallback(
    async (documentId: string): Promise<boolean> => {
      const payload = await loadDocumentPdfFile(documentId);
      if (!payload) return false;
      try {
        u3141OpenDocumentPdfFileInNewTab(payload);
        return true;
      } catch (error) {
        onError?.({ error });
        return false;
      }
    },
    [loadDocumentPdfFile, onError]
  );

  const loadDocumentPdfFileAsHtmlText = useCallback(
    async (documentId: string): Promise<string | null> => {
      const payload = await loadDocumentPdfFile(documentId);
      if (!payload) return null;
      const mime = u3141InferDocumentMime(payload.buffer, payload.contentType);
      if (!u3141IsHtmlMime(mime)) {
        return null;
      }
      return u3141DecodeDocumentPdfFileAsText(payload);
    },
    [loadDocumentPdfFile]
  );

  return { openDocumentPdfFile, loadDocumentPdfFile, loadDocumentPdfFileAsHtmlText };
}
